import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ContextMenuState, SlamFloorplanCanvasProps, SlamPoint } from '../../robot-position-setup/types'
import { useSlamTransform } from './useSlamTransform'
import { clamp, drawGridAndOrigin, drawHome, drawMarkers, drawObstacles, drawPendingMarker, drawRobot } from '../../robot-position-setup/canvasHelpers'

const MIN_SCALE = 1
const MAX_SCALE = 30
const MIN_OVERLAY_SCALE = 0.4
const MAX_OVERLAY_SCALE = 3

export function useSlamCanvasInteraction({
    backgroundImageUrl = null,
    backgroundWidthM,
    backgroundHeightM,
    slamJson,
    markers = [],
    selectedMarkerId = null,
    rotateMode = false,
    rotateMarkerId = null,
    pendingMarker,
    directionOverlayMarkerId = null,
    directionOverlayYaw = null,
    mapRotation = 0,
    isAddmode = false,
    homePlacementLock = false,
    homePlacementPoint = null,
    onSlamClick,
    onMarkerClick,
    onMarkerYawChange,
    onMarkerYawCommit,
    onConfirmAddNew,
}: SlamFloorplanCanvasProps) {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const pathRef = useRef<Path2D | null>(null)
    const backgroundImageRef = useRef<HTMLImageElement | null>(null)

    const [scale, setScale] = useState(1)
    const [pan, setPan] = useState({ x: 0, y: 0 })
    const [slamOffset, setSlamOffset] = useState({ x: 0, y: 0 })
    const [overlayScale, setOverlayScale] = useState(1)
    const [overlayRotationRad, setOverlayRotationRad] = useState(0)
    const [hasBackgroundImage, setHasBackgroundImage] = useState(false)
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null)
    const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null)
    const [mousePos, setMousePos] = useState<{ clientX: number; clientY: number; altKey: boolean; shiftKey: boolean } | null>(null)
    const [hoveredCoords, setHoveredCoords] = useState<SlamPoint | null>(null)
    const [homePlacementDraftPoint, setHomePlacementDraftPoint] = useState<SlamPoint | null>(null)

    const dragRef = useRef({ dragging: false, startX: 0, startY: 0, panX: 0, panY: 0, moved: false })
    const alignDragRef = useRef({ dragging: false, startX: 0, startY: 0, offsetX: 0, offsetY: 0 })
    const overlayRotateRef = useRef({ rotating: false, startAngle: 0, startRotation: 0 })
    const overlayRotateRafRef = useRef<number | null>(null)
    const overlayRotateNextAngleRef = useRef<number | null>(null)
    const rotateRef = useRef({ rotating: false, markerId: '', lastYaw: 0 })
    const homePlacementDragRef = useRef({ dragging: false, moved: false })
    const prevSizeRef = useRef<{ width: number; height: number } | null>(null)
    const renderCanvasRef = useRef<(canvas: HTMLCanvasElement) => void>(() => { })
    const appliedHomePlacementKeyRef = useRef<string | null>(null)
    const preHomeSlamOffsetRef = useRef({ x: 0, y: 0 })

    const {
        normalizedObstacles,
        slamToScreenPx,
        screenPxToSlam,
        getPixelsPerMeter,
        kShrink,
        pxToCtx,
    } = useSlamTransform(
        slamJson,
        mapRotation,
        scale,
        typeof backgroundWidthM === 'number' &&
            typeof backgroundHeightM === 'number' &&
            Number.isFinite(backgroundWidthM) &&
            Number.isFinite(backgroundHeightM) &&
            backgroundWidthM > 0 &&
            backgroundHeightM > 0
            ? { width: backgroundWidthM, height: backgroundHeightM }
            : null,
    )

    const robot = useMemo(() => {
        if (!slamJson.robot) return null
        const r = slamJson.robot
        return { ...r, x: r.x ?? 0, y: r.y ?? 0, yawRad: ((r.yawDeg ?? 0) * Math.PI) / 180 }
    }, [slamJson.robot])

    const home = useMemo(() => {
        if (!slamJson.home) return null
        const h = slamJson.home
        return { ...h, x: h.x ?? 0, y: h.y ?? 0, yawRad: ((h.yawDeg ?? 0) * Math.PI) / 180 }
    }, [slamJson.home])

    const temporaryHome = useMemo(() => {
        if (homePlacementPoint) {
            return {
                x: homePlacementPoint.x,
                y: homePlacementPoint.y,
                yawRad: home?.yawRad ?? 0,
            }
        }

        if (home) {
            return {
                x: home.x,
                y: home.y,
                yawRad: home.yawRad,
            }
        }

        const bounds = slamJson.bounds
        const minX = bounds?.minX ?? -10
        const maxX = bounds?.maxX ?? 10
        const minY = bounds?.minY ?? -10
        const maxY = bounds?.maxY ?? 10

        return {
            x: (minX + maxX) / 2,
            y: (minY + maxY) / 2,
            yawRad: 0,
        }
    }, [homePlacementPoint, home, slamJson.bounds])

    const processedMarkers = useMemo(() => {
        return markers.map((m) => {
            const rx = m.rawX ?? m.x
            const ry = m.rawY ?? m.y
            return { ...m, x: rx, y: ry, rawX: rx, rawY: ry, yaw: m.yaw ?? 0 }
        })
    }, [markers])

    const clampPan = useCallback((nextScale: number, nextPan: { x: number; y: number }, rect: DOMRect) => {
        const diffX = rect.width * nextScale - rect.width
        const diffY = rect.height * nextScale - rect.height
        return {
            x: clamp(nextPan.x, -diffX, 0),
            y: clamp(nextPan.y, -diffY, 0),
        }
    }, [])

    const isSamePoint = useCallback((a: SlamPoint | null, b: SlamPoint | null) => {
        if (!a || !b) return false
        return Math.abs(a.x - b.x) <= 0.00001 && Math.abs(a.y - b.y) <= 0.00001
    }, [])

    const requiresHomePlacement = !!homePlacementLock
    const activeHomePlacementPoint = useMemo(() => {
        if (!requiresHomePlacement || !homePlacementDraftPoint) {
            return temporaryHome
        }

        return {
            ...temporaryHome,
            x: homePlacementDraftPoint.x,
            y: homePlacementDraftPoint.y,
        }
    }, [requiresHomePlacement, homePlacementDraftPoint, temporaryHome])

    const getOverlayBaseAnchorPx = useCallback(
        (width: number, height: number) => {
            if (home) {
                return slamToScreenPx(home, width, height)
            }
            if (robot) {
                return slamToScreenPx(robot, width, height)
            }
            return { x: width / 2, y: height / 2 }
        },
        [home, robot, slamToScreenPx],
    )

    const getOverlayPivotPx = useCallback(
        (width: number, height: number) => {
            const baseAnchor = getOverlayBaseAnchorPx(width, height)
            return {
                x: baseAnchor.x + slamOffset.x,
                y: baseAnchor.y + slamOffset.y,
            }
        },
        [getOverlayBaseAnchorPx, slamOffset],
    )

    const slamToScreenPxWithOffset = useCallback(
        (point: SlamPoint, width: number, height: number) => {
            const px = slamToScreenPx(point, width, height)
            const baseAnchor = getOverlayBaseAnchorPx(width, height)
            const pivot = getOverlayPivotPx(width, height)
            const dx = (px.x - baseAnchor.x) * overlayScale
            const dy = (px.y - baseAnchor.y) * overlayScale
            const cos = Math.cos(overlayRotationRad)
            const sin = Math.sin(overlayRotationRad)
            return {
                x: pivot.x + dx * cos - dy * sin,
                y: pivot.y + dx * sin + dy * cos,
            }
        },
        [slamToScreenPx, getOverlayBaseAnchorPx, getOverlayPivotPx, overlayScale, overlayRotationRad],
    )

    const baseToSlamPoint = useCallback(
        (baseX: number, baseY: number, width: number, height: number) => {
            const baseAnchor = getOverlayBaseAnchorPx(width, height)
            const pivot = getOverlayPivotPx(width, height)
            const x = baseX
            const y = baseY

            const cos = Math.cos(-overlayRotationRad)
            const sin = Math.sin(-overlayRotationRad)
            const rdx = x - pivot.x
            const rdy = y - pivot.y
            const unrotX = rdx * cos - rdy * sin
            const unrotY = rdx * sin + rdy * cos
            const unscaledX = baseAnchor.x + unrotX / overlayScale
            const unscaledY = baseAnchor.y + unrotY / overlayScale

            return screenPxToSlam(unscaledX, unscaledY, width, height)
        },
        [screenPxToSlam, getOverlayBaseAnchorPx, getOverlayPivotPx, overlayScale, overlayRotationRad],
    )

    const getMarkerScreenCenter = useCallback(
        (markerId: string, width: number, height: number) => {
            const targetPoint = markerId === 'home'
                ? (requiresHomePlacement ? activeHomePlacementPoint : home)
                : processedMarkers.find((m) => m.id === markerId)
            if (!targetPoint) return null

            const basePx = slamToScreenPxWithOffset(targetPoint, width, height)
            const offsetY = markerId === 'home' ? 0 : pxToCtx(14)

            return {
                x: pan.x + basePx.x * scale,
                y: pan.y + (basePx.y - offsetY) * scale,
            }
        },
        [home, requiresHomePlacement, activeHomePlacementPoint, processedMarkers, slamToScreenPxWithOffset, pan, scale, pxToCtx],
    )

    const checkMarkerHit = useCallback(
        (clientX: number, clientY: number) => {
            const el = containerRef.current
            if (!el) return null
            const rect = el.getBoundingClientRect()
            if (rect.width <= 0 || rect.height <= 0) return null

            const mouseX = clientX - rect.left
            const mouseY = clientY - rect.top
            const hitRadius = Math.max(7, 8 * kShrink)

            const checkHit = (point: SlamPoint, radiusExtra = 0) => {
                const px = slamToScreenPxWithOffset(point, rect.width, rect.height)
                const screenX = px.x * scale + pan.x
                const screenY = px.y * scale + pan.y
                return Math.hypot(mouseX - screenX, mouseY - screenY) <= hitRadius + radiusExtra
            }

            for (const marker of processedMarkers) {
                if (checkHit(marker)) return marker.id
            }
            if (home && checkHit(home)) return 'home'
            if (pendingMarker && checkHit(pendingMarker)) return 'pending'

            return null
        },
        [home, processedMarkers, pendingMarker, scale, pan, kShrink, slamToScreenPxWithOffset],
    )

    const checkHomePlacementMarkerHit = useCallback(
        (clientX: number, clientY: number) => {
            const el = containerRef.current
            if (!el) return false

            const rect = el.getBoundingClientRect()
            if (rect.width <= 0 || rect.height <= 0) return false

            const px = slamToScreenPxWithOffset(activeHomePlacementPoint, rect.width, rect.height)
            const markerX = px.x * scale + pan.x
            const markerY = px.y * scale + pan.y
            const mouseX = clientX - rect.left
            const mouseY = clientY - rect.top
            const hitRadius = Math.max(10, 11 * kShrink)

            return Math.hypot(mouseX - markerX, mouseY - markerY) <= hitRadius
        },
        [activeHomePlacementPoint, slamToScreenPxWithOffset, scale, pan, kShrink],
    )

    const getCentralMapRect = useCallback(
        (rect: DOMRect) => {
            const bgImage = backgroundImageRef.current
            if (
                !bgImage ||
                !bgImage.complete ||
                bgImage.naturalWidth <= 0 ||
                bgImage.naturalHeight <= 0
            ) {
                return null
            }

            const hasMetricSize =
                typeof backgroundWidthM === 'number' &&
                typeof backgroundHeightM === 'number' &&
                Number.isFinite(backgroundWidthM) &&
                Number.isFinite(backgroundHeightM) &&
                backgroundWidthM > 0 &&
                backgroundHeightM > 0

            let drawW = 0
            let drawH = 0
            if (hasMetricSize) {
                const ppm = getPixelsPerMeter(rect.width, rect.height)
                drawW = backgroundWidthM * ppm
                drawH = backgroundHeightM * ppm
            } else {
                const fitScale = Math.min(
                    rect.width / bgImage.naturalWidth,
                    rect.height / bgImage.naturalHeight,
                )
                drawW = bgImage.naturalWidth * fitScale
                drawH = bgImage.naturalHeight * fitScale
            }

            const drawX = (rect.width - drawW) / 2
            const drawY = (rect.height - drawH) / 2

            return {
                left: drawX,
                top: drawY,
                right: drawX + drawW,
                bottom: drawY + drawH,
            }
        },
        [backgroundHeightM, backgroundWidthM, getPixelsPerMeter],
    )

    const checkInsideCentralMapArea = useCallback(
        (clientX: number, clientY: number): boolean => {
            const el = containerRef.current
            if (!el) return true

            const rect = el.getBoundingClientRect()
            if (rect.width <= 0 || rect.height <= 0) return true

            const centralRect = getCentralMapRect(rect)
            if (!centralRect) return true

            const viewportX = clientX - rect.left
            const viewportY = clientY - rect.top

            return (
                viewportX >= centralRect.left * scale + pan.x &&
                viewportX <= centralRect.right * scale + pan.x &&
                viewportY >= centralRect.top * scale + pan.y &&
                viewportY <= centralRect.bottom * scale + pan.y
            )
        },
        [getCentralMapRect, scale, pan],
    )

    const renderCanvas = useCallback(
        (canvas: HTMLCanvasElement) => {
            const ctx = canvas.getContext('2d')
            if (!ctx) return

            const dpr = window.devicePixelRatio || 1
            const rect = canvas.getBoundingClientRect()
            if (rect.width <= 0 || rect.height <= 0) return

            canvas.width = rect.width * dpr
            canvas.height = rect.height * dpr

            ctx.setTransform(dpr * scale, 0, 0, dpr * scale, dpr * pan.x, dpr * pan.y)
            ctx.clearRect(0, 0, rect.width / scale, rect.height / scale)

            ctx.fillStyle = '#ffffff'
            ctx.fillRect(0, 0, rect.width, rect.height)

            const bgImage = backgroundImageRef.current
            if (bgImage && bgImage.complete && bgImage.naturalWidth > 0 && bgImage.naturalHeight > 0) {
                const hasMetricSize =
                    typeof backgroundWidthM === 'number' &&
                    typeof backgroundHeightM === 'number' &&
                    Number.isFinite(backgroundWidthM) &&
                    Number.isFinite(backgroundHeightM) &&
                    backgroundWidthM > 0 &&
                    backgroundHeightM > 0

                let drawW = 0
                let drawH = 0
                if (hasMetricSize) {
                    const ppm = getPixelsPerMeter(rect.width, rect.height)
                    drawW = backgroundWidthM * ppm
                    drawH = backgroundHeightM * ppm
                } else {
                    const fitScale = Math.min(rect.width / bgImage.naturalWidth, rect.height / bgImage.naturalHeight)
                    drawW = bgImage.naturalWidth * fitScale
                    drawH = bgImage.naturalHeight * fitScale
                }

                const drawX = (rect.width - drawW) / 2
                const drawY = (rect.height - drawH) / 2

                ctx.save()
                ctx.globalAlpha = 0.45
                ctx.drawImage(bgImage, drawX, drawY, drawW, drawH)
                ctx.restore()
            }

            const drawCtx = { ctx, width: rect.width, height: rect.height, slamToScreenPx: slamToScreenPxWithOffset, pxToCtx }

            if (!requiresHomePlacement) {
                drawObstacles(drawCtx, normalizedObstacles, pathRef)
                drawGridAndOrigin(drawCtx)
                drawRobot(drawCtx, robot)

                const isHomeSelected = selectedMarkerId === 'home' || hoveredMarkerId === 'home'
                let homeEffectiveYaw = home?.yawRad ?? 0
                if (rotateRef.current.rotating && rotateRef.current.markerId === 'home') {
                    homeEffectiveYaw = rotateRef.current.lastYaw
                } else if (
                    directionOverlayMarkerId === 'home' &&
                    typeof directionOverlayYaw === 'number' &&
                    Number.isFinite(directionOverlayYaw)
                ) {
                    homeEffectiveYaw = directionOverlayYaw
                }
                drawHome(drawCtx, home, {
                    isSelected: isHomeSelected,
                    showDirectionOverlay: directionOverlayMarkerId === 'home',
                    effectiveYaw: homeEffectiveYaw,
                })

                drawMarkers(drawCtx, processedMarkers, selectedMarkerId, hoveredMarkerId, rotateRef, directionOverlayMarkerId, directionOverlayYaw)
                drawPendingMarker(drawCtx, pendingMarker ?? null)
                return
            }

            drawHome(drawCtx, activeHomePlacementPoint, {
                isSelected: true,
                showDirectionOverlay: true,
                effectiveYaw:
                    rotateRef.current.rotating && rotateRef.current.markerId === 'home'
                        ? rotateRef.current.lastYaw
                        : activeHomePlacementPoint.yawRad,
            })
        },
        [scale, pan, normalizedObstacles, slamToScreenPxWithOffset, robot, home, selectedMarkerId, hoveredMarkerId, directionOverlayMarkerId, directionOverlayYaw, processedMarkers, pendingMarker, pxToCtx, getPixelsPerMeter, backgroundWidthM, backgroundHeightM, requiresHomePlacement, activeHomePlacementPoint],
    )

    useEffect(() => {
        renderCanvasRef.current = renderCanvas
    }, [renderCanvas])

    useEffect(() => {
        if (!backgroundImageUrl) {
            backgroundImageRef.current = null
            setHasBackgroundImage(false)
            return
        }

        const img = new Image()
        img.onload = () => {
            backgroundImageRef.current = img
            setHasBackgroundImage(true)
            if (canvasRef.current) renderCanvasRef.current(canvasRef.current)
        }
        img.onerror = () => {
            backgroundImageRef.current = null
            setHasBackgroundImage(false)
        }
        img.src = backgroundImageUrl

        return () => {
            img.onload = null
            img.onerror = null
            backgroundImageRef.current = null
            setHasBackgroundImage(false)
        }
    }, [backgroundImageUrl])

    useEffect(() => {
        if (requiresHomePlacement) {
            if (appliedHomePlacementKeyRef.current !== null) {
                appliedHomePlacementKeyRef.current = null
                // Về lại mốc trước lần lưu gần nhất, để Update bắt đầu cùng gốc tọa độ như Add
                setSlamOffset(preHomeSlamOffsetRef.current)
            }
            return
        }

        if (homePlacementDraftPoint) {
            setHomePlacementDraftPoint(null)
        }

        if (!homePlacementPoint || !home || !containerRef.current) return

        const key = `${homePlacementPoint.x.toFixed(6)}:${homePlacementPoint.y.toFixed(6)}`
        if (appliedHomePlacementKeyRef.current === key) return

        const rect = containerRef.current.getBoundingClientRect()
        if (rect.width <= 0 || rect.height <= 0) return

        preHomeSlamOffsetRef.current = slamOffset
        const targetPx = slamToScreenPx(homePlacementPoint, rect.width, rect.height)
        const currentHomePx = slamToScreenPxWithOffset(home, rect.width, rect.height)

        setSlamOffset((prev) => ({
            x: prev.x + (targetPx.x - currentHomePx.x),
            y: prev.y + (targetPx.y - currentHomePx.y),
        }))
        appliedHomePlacementKeyRef.current = key
    }, [requiresHomePlacement, homePlacementPoint, home, slamToScreenPx, slamToScreenPxWithOffset, homePlacementDraftPoint, slamOffset])

    // Effects: Resize & Drawing triggers
    useEffect(() => {
        if (!containerRef.current) return
        const el = containerRef.current

        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect
                if (width <= 0 || height <= 0) continue

                if (prevSizeRef.current) {
                    const { width: oldW, height: oldH } = prevSizeRef.current
                    if (oldW !== width || oldH !== height) {
                        setPan((prevPan) => {
                            const newPanX = prevPan.x + ((width - oldW) / 2) * (1 - scale)
                            const newPanY = prevPan.y + ((height - oldH) / 2) * (1 - scale)
                            return clampPan(scale, { x: newPanX, y: newPanY }, entry.contentRect)
                        })
                    }
                }
                prevSizeRef.current = { width, height }
            }
            if (canvasRef.current) renderCanvas(canvasRef.current)
        })

        ro.observe(el)
        return () => ro.disconnect()
    }, [scale, clampPan, renderCanvas])

    useEffect(() => {
        if (canvasRef.current) renderCanvas(canvasRef.current)
    }, [renderCanvas])

    useEffect(() => {
        const handleWindowClick = () => setContextMenu(null)
        window.addEventListener('click', handleWindowClick)
        return () => window.removeEventListener('click', handleWindowClick)
    }, [])

    useEffect(() => {
        if (rotateMode) setContextMenu(null)
    }, [rotateMode])

    const isInDonutArea = useCallback(
        (e: { clientX: number; clientY: number }, markerId: string): boolean => {
            const el = containerRef.current
            if (!el) return false
            const rect = el.getBoundingClientRect()
            const center = getMarkerScreenCenter(markerId, rect.width, rect.height)
            if (!center) return false

            const [innerR, outerR] = markerId === 'home' ? [13, 28] : [16, 32]
            const dist = Math.hypot(e.clientX - rect.left - center.x, e.clientY - rect.top - center.y)
            return dist >= innerR && dist <= outerR
        },
        [getMarkerScreenCenter],
    )

    const yawFromPointer = useCallback(
        (markerId: string, clientX: number, clientY: number) => {
            const el = containerRef.current
            if (!el) return null
            const rect = el.getBoundingClientRect()
            const center = getMarkerScreenCenter(markerId, rect.width, rect.height)
            if (!center) return null

            const dx = clientX - rect.left - center.x
            const dy = clientY - rect.top - center.y
            const rad = Math.atan2(dy, dx) + Math.PI / 2
            return (rad + 2 * Math.PI) % (2 * Math.PI)
        },
        [getMarkerScreenCenter],
    )

    const angleAroundCanvasCenter = useCallback((clientX: number, clientY: number) => {
        const el = containerRef.current
        if (!el) return null

        const rect = el.getBoundingClientRect()
        if (rect.width <= 0 || rect.height <= 0) return null

        const baseX = (clientX - rect.left - pan.x) / scale
        const baseY = (clientY - rect.top - pan.y) / scale
        const pivot = getOverlayPivotPx(rect.width, rect.height)
        const dx = baseX - pivot.x
        const dy = baseY - pivot.y

        return Math.atan2(dy, dx)
    }, [pan.x, pan.y, scale, getOverlayPivotPx])

    const normalizedAngleDelta = useCallback((current: number, start: number) => {
        return Math.atan2(Math.sin(current - start), Math.cos(current - start))
    }, [])

    const scheduleOverlayRotation = useCallback((nextAngle: number) => {
        overlayRotateNextAngleRef.current = nextAngle

        if (overlayRotateRafRef.current !== null) return

        overlayRotateRafRef.current = window.requestAnimationFrame(() => {
            overlayRotateRafRef.current = null
            const currentAngle = overlayRotateNextAngleRef.current
            if (currentAngle === null) return

            const start = overlayRotateRef.current.startAngle
            const baseRotation = overlayRotateRef.current.startRotation
            const delta = normalizedAngleDelta(currentAngle, start)
            setOverlayRotationRad(baseRotation + delta)
        })
    }, [normalizedAngleDelta])

    useEffect(() => {
        return () => {
            if (overlayRotateRafRef.current !== null) {
                window.cancelAnimationFrame(overlayRotateRafRef.current)
            }
        }
    }, [])

    useEffect(() => {
        const container = containerRef.current
        if (!container || !mousePos || dragRef.current.dragging || alignDragRef.current.dragging || overlayRotateRef.current.rotating) return

        if (rotateMode) {
            container.style.cursor = rotateMarkerId && isInDonutArea(mousePos, rotateMarkerId) ? 'grab' : (checkInsideCentralMapArea(mousePos.clientX, mousePos.clientY) ? 'crosshair' : 'not-allowed')
            return
        }

        if (requiresHomePlacement) {
            if (checkHomePlacementMarkerHit(mousePos.clientX, mousePos.clientY) || isInDonutArea(mousePos, 'home')) {
                container.style.cursor = 'grab'
                return
            }
            container.style.cursor = checkInsideCentralMapArea(mousePos.clientX, mousePos.clientY) ? 'crosshair' : 'not-allowed'
            return
        }

        if (hoveredMarkerId) {
            container.style.cursor = 'pointer'
            return
        }

        container.style.cursor = checkInsideCentralMapArea(mousePos.clientX, mousePos.clientY)
            ? scale > 1
                ? 'grab'
                : 'crosshair'
            : 'not-allowed'
    }, [mousePos, hoveredMarkerId, rotateMode, rotateMarkerId, scale, checkInsideCentralMapArea, isInDonutArea, requiresHomePlacement, checkHomePlacementMarkerHit])

    useEffect(() => {
        if (!mousePos || dragRef.current.dragging || alignDragRef.current.dragging || overlayRotateRef.current.rotating || requiresHomePlacement) return
        const updateHoverState = () => {
            const hitId = checkMarkerHit(mousePos.clientX, mousePos.clientY)
            setHoveredMarkerId((prev) => (prev !== hitId ? hitId : prev))

            const el = containerRef.current
            if (el) {
                const rect = el.getBoundingClientRect()
                const baseX = (mousePos.clientX - rect.left - pan.x) / scale
                const baseY = (mousePos.clientY - rect.top - pan.y) / scale
                setHoveredCoords(baseToSlamPoint(baseX, baseY, rect.width, rect.height))
            }
        }
        const animFrameId = requestAnimationFrame(updateHoverState)
        return () => cancelAnimationFrame(animFrameId)
    }, [mousePos, checkMarkerHit, pan, scale, baseToSlamPoint, requiresHomePlacement])

    const zoomBy = useCallback((nextScale: number, anchorClientX: number, anchorClientY: number) => {
        const el = containerRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        if (rect.width <= 0 || rect.height <= 0) return

        const anchorX = anchorClientX - rect.left
        const anchorY = anchorClientY - rect.top
        const nextPanX = anchorX - nextScale * ((anchorX - pan.x) / scale)
        const nextPanY = anchorY - nextScale * ((anchorY - pan.y) / scale)

        setScale(nextScale)
        setPan(clampPan(nextScale, { x: nextPanX, y: nextPanY }, rect))
    }, [scale, pan, clampPan])

    useEffect(() => {
        const el = containerRef.current
        if (!el) return
        const handleWheel = (e: WheelEvent) => {
            if (requiresHomePlacement) { e.preventDefault(); return }

            if (rotateMode) { e.preventDefault(); return }

            if (hasBackgroundImage && e.shiftKey) {
                e.preventDefault()
                const factor = e.deltaY > 0 ? 0.97 : 1.03
                setOverlayScale((prev) => clamp(prev * factor, MIN_OVERLAY_SCALE, MAX_OVERLAY_SCALE))
                return
            }

            e.preventDefault()

            const zoomFactor = e.deltaY > 0 ? 0.88 : 1.15
            const nextScale = clamp(scale * zoomFactor, MIN_SCALE, MAX_SCALE)

            zoomBy(nextScale, e.clientX, e.clientY)
        }
        el.addEventListener('wheel', handleWheel, { passive: false })
        return () => el.removeEventListener('wheel', handleWheel)
    }, [rotateMode, scale, zoomBy, hasBackgroundImage, requiresHomePlacement])

    // Mouse / Pointer Handlers
    const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.shiftKey) {
            return
        }

        if ((rotateMode && !isAddmode) || dragRef.current.moved) {
            dragRef.current.moved = false
            return
        }

        if (homePlacementDragRef.current.moved) {
            homePlacementDragRef.current.moved = false
            return
        }

        if (requiresHomePlacement) {
            if (!checkInsideCentralMapArea(e.clientX, e.clientY)) return

            const el = containerRef.current
            if (!el) return
            const rect = el.getBoundingClientRect()
            const baseX = (e.clientX - rect.left - pan.x) / scale
            const baseY = (e.clientY - rect.top - pan.y) / scale
            const nextPoint = baseToSlamPoint(baseX, baseY, rect.width, rect.height)
            setHomePlacementDraftPoint(null)
            onSlamClick?.(nextPoint)
            return
        }

        const hitResult = checkMarkerHit(e.clientX, e.clientY)
        if (hitResult && hitResult !== 'pending' && hitResult !== 'temp_add_id') {
            onMarkerClick?.(hitResult)
            return
        }

        if (!checkInsideCentralMapArea(e.clientX, e.clientY)) return

        const el = containerRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        const baseX = (e.clientX - rect.left - pan.x) / scale
        const baseY = (e.clientY - rect.top - pan.y) / scale
        onSlamClick?.(baseToSlamPoint(baseX, baseY, rect.width, rect.height))
    }

    const onContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault()
        if (rotateMode || requiresHomePlacement) { setContextMenu(null); return }

        const rect = e.currentTarget.getBoundingClientRect()
        const hitResult = checkMarkerHit(e.clientX, e.clientY)
        if (!hitResult) { setContextMenu(null); return }

        const clickCoords = baseToSlamPoint(
            (e.clientX - rect.left - pan.x) / scale,
            (e.clientY - rect.top - pan.y) / scale,
            rect.width,
            rect.height,
        )

        if (hitResult === 'pending') {
            setContextMenu({ mouseX: e.clientX - rect.left, mouseY: e.clientY - rect.top, markerId: null, hasName: false, clickCoords })
        } else {
            if (hitResult !== 'home' && (!selectedMarkerId || hitResult !== selectedMarkerId)) {
                setContextMenu(null)
                return
            }
            setContextMenu({ mouseX: e.clientX - rect.left, mouseY: e.clientY - rect.top, markerId: hitResult, hasName: true, clickCoords })
        }
    }

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!containerRef.current || e.button !== 0) return
        dragRef.current.moved = false

        if (requiresHomePlacement) {
            homePlacementDragRef.current.moved = false
            if (isInDonutArea(e, 'home')) {
                const yaw = yawFromPointer('home', e.clientX, e.clientY)
                if (yaw !== null) {
                    dragRef.current.moved = true
                    rotateRef.current = { rotating: true, markerId: 'home', lastYaw: yaw }
                    onMarkerYawChange?.('home', yaw)
                    if (canvasRef.current) renderCanvas(canvasRef.current)
                    containerRef.current.style.cursor = 'grabbing'
                    return
                }
            }
            if (checkHomePlacementMarkerHit(e.clientX, e.clientY)) {
                homePlacementDragRef.current.dragging = true
                setHomePlacementDraftPoint(activeHomePlacementPoint)
                containerRef.current.style.cursor = 'grabbing'
                try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* ignore */ }
            }
            return
        }

        if (rotateMode && rotateMarkerId) {
            if (isInDonutArea(e, rotateMarkerId)) {
                dragRef.current.moved = true
                const yaw = yawFromPointer(rotateMarkerId, e.clientX, e.clientY)
                if (yaw !== null) {
                    rotateRef.current = { rotating: true, markerId: rotateMarkerId, lastYaw: yaw }
                    onMarkerYawChange?.(rotateMarkerId, yaw)
                    if (canvasRef.current) renderCanvas(canvasRef.current)
                    containerRef.current.style.cursor = 'grabbing'
                    return
                }
            }
            if (!isAddmode) return
        }

        if (e.shiftKey) {
            const startAngle = angleAroundCanvasCenter(e.clientX, e.clientY)
            if (startAngle !== null) {
                overlayRotateRef.current = {
                    rotating: true,
                    startAngle,
                    startRotation: overlayRotationRad,
                }
                dragRef.current.moved = true
                containerRef.current.style.cursor = 'grabbing'
                try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* ignore */ }
                return
            }
        }

        if (scale > 1) {
            dragRef.current.dragging = true
            dragRef.current.startX = e.clientX
            dragRef.current.startY = e.clientY
            dragRef.current.panX = pan.x
            dragRef.current.panY = pan.y
            containerRef.current.style.cursor = 'grabbing'
            try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* ignore */ }
        }
    }

    const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        setMousePos({ clientX: e.clientX, clientY: e.clientY, altKey: e.altKey, shiftKey: e.shiftKey })

        if (homePlacementDragRef.current.dragging) {
            const el = containerRef.current
            if (!el) return
            const rect = el.getBoundingClientRect()
            const baseX = (e.clientX - rect.left - pan.x) / scale
            const baseY = (e.clientY - rect.top - pan.y) / scale
            homePlacementDragRef.current.moved = true
            const nextPoint = baseToSlamPoint(baseX, baseY, rect.width, rect.height)
            setHomePlacementDraftPoint((prev) => (isSamePoint(prev, nextPoint) ? prev : nextPoint))
            return
        }

        if (rotateRef.current.rotating && rotateRef.current.markerId) {
            dragRef.current.moved = true
            const yaw = yawFromPointer(rotateRef.current.markerId, e.clientX, e.clientY)
            if (yaw !== null) {
                rotateRef.current.lastYaw = yaw
                if (canvasRef.current) renderCanvas(canvasRef.current)
            }
            return
        }

        if (overlayRotateRef.current.rotating) {
            dragRef.current.moved = true
            const nextAngle = angleAroundCanvasCenter(e.clientX, e.clientY)
            if (nextAngle !== null) {
                scheduleOverlayRotation(nextAngle)
            }
            return
        }

        if (alignDragRef.current.dragging) {
            const dx = e.clientX - alignDragRef.current.startX
            const dy = e.clientY - alignDragRef.current.startY
            if (Math.abs(dx) > 1 || Math.abs(dy) > 1) dragRef.current.moved = true
            setSlamOffset({
                x: alignDragRef.current.offsetX + dx / scale,
                y: alignDragRef.current.offsetY + dy / scale,
            })
            return
        }

        if (!dragRef.current.dragging) return

        const dx = e.clientX - dragRef.current.startX
        const dy = e.clientY - dragRef.current.startY
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) dragRef.current.moved = true

        const el = containerRef.current
        if (!el) return
        const rect = el.getBoundingClientRect()
        setPan(clampPan(scale, { x: dragRef.current.panX + dx, y: dragRef.current.panY + dy }, rect))
    }

    const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
        if (homePlacementDragRef.current.dragging) {
            homePlacementDragRef.current.dragging = false
            if (homePlacementDraftPoint) {
                onSlamClick?.(homePlacementDraftPoint)
            }
            if (containerRef.current) containerRef.current.style.cursor = 'grab'
            try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* ignore */ }
            return
        }

        if (rotateRef.current.rotating) {
            rotateRef.current.rotating = false
            const { markerId, lastYaw } = rotateRef.current
            if (markerId) void onMarkerYawCommit?.(markerId, (lastYaw + 2 * Math.PI) % (2 * Math.PI))
            if (containerRef.current) containerRef.current.style.cursor = 'grab'
            if (canvasRef.current) renderCanvas(canvasRef.current)
            return
        }

        if (overlayRotateRef.current.rotating) {
            overlayRotateRef.current.rotating = false
            if (containerRef.current) containerRef.current.style.cursor = 'grab'
            try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* ignore */ }
            return
        }

        if (alignDragRef.current.dragging) {
            alignDragRef.current.dragging = false
            if (containerRef.current) {
                containerRef.current.style.cursor = hasBackgroundImage ? 'grab' : 'default'
            }
            try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* ignore */ }
            return
        }

        if (!dragRef.current.dragging) return
        dragRef.current.dragging = false
        if (containerRef.current) {
            containerRef.current.style.cursor = scale > 1 ? 'grab' : 'default'
        }
        try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* ignore */ }
    }

    return {
        containerRef,
        canvasRef,
        scale,
        slamOffset,
        overlayScale,
        overlayRotationRad,
        canZoomIn: scale < MAX_SCALE,
        canZoomOut: scale > MIN_SCALE,
        canIncreaseOverlayScale: overlayScale < MAX_OVERLAY_SCALE,
        canDecreaseOverlayScale: overlayScale > MIN_OVERLAY_SCALE,
        hasBackgroundImage,
        contextMenu,
        hoveredCoords,
        setContextMenu,
        handlers: {
            onClick,
            onContextMenu,
            onPointerDown,
            onPointerMove,
            onPointerUp,
        },
        actions: {
            onConfirmAddNew,
            onZoomIn: () => {
                const el = containerRef.current
                if (!el) return
                const rect = el.getBoundingClientRect()
                const nextScale = clamp(scale * 1.15, MIN_SCALE, MAX_SCALE)
                zoomBy(nextScale, rect.left + rect.width / 2, rect.top + rect.height / 2)
            },
            onZoomOut: () => {
                const el = containerRef.current
                if (!el) return
                const rect = el.getBoundingClientRect()
                const nextScale = clamp(scale * 0.88, MIN_SCALE, MAX_SCALE)
                zoomBy(nextScale, rect.left + rect.width / 2, rect.top + rect.height / 2)
            },
            onRotateOverlayLeft: () => {
                setOverlayRotationRad((prev) => prev - 0.04)
            },
            onRotateOverlayRight: () => {
                setOverlayRotationRad((prev) => prev + 0.04)
            },
            onIncreaseOverlayScale: () => {
                setOverlayScale((prev) => clamp(prev * 1.05, MIN_OVERLAY_SCALE, MAX_OVERLAY_SCALE))
            },
            onDecreaseOverlayScale: () => {
                setOverlayScale((prev) => clamp(prev * 0.95, MIN_OVERLAY_SCALE, MAX_OVERLAY_SCALE))
            },
            onResetOverlayScale: () => {
                setOverlayScale(1)
            },
            onResetOverlayRotation: () => {
                setOverlayRotationRad(0)
            },
            onMoveRobot: (onMoveRobot: (yaw: number) => void) => {
                let targetYaw = 0
                if (contextMenu?.markerId === 'home') {
                    targetYaw = slamJson.home?.yawDeg ?? 0
                } else if (contextMenu?.markerId) {
                    const officialMarker = markers.find((m) => m.id === contextMenu.markerId)
                    if (officialMarker && typeof officialMarker.yaw === 'number') {
                        targetYaw = officialMarker.yaw
                    }
                }
                onMoveRobot(targetYaw)
                setContextMenu(null)
            },
            onGoHome: (onGoHome: () => void) => {
                onGoHome()
                setContextMenu(null)
            },
            onResetSlamOverlayOffset: () => {
                setSlamOffset({ x: 0, y: 0 })
                setOverlayScale(1)
                setOverlayRotationRad(0)
                appliedHomePlacementKeyRef.current = null
            },
            onResetZoom: () => {
                setScale(1)
                setPan({ x: 0, y: 0 })
            },
        },
        requiresHomePlacement,
    }
}